'use client';

import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { createClient } from '../lib/supabase/client';
import { setActiveUserId } from '../lib/db';
import { safeStorage } from '../lib/safe-storage';

export type UserProfile = {
  id: string;
  full_name: string;
  email: string;
  cdc_number?: string;
  rank?: string;
};

type AuthContextType = {
  user: any;
  profile: UserProfile | null;
  session: any;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signInWithGoogle: () => Promise<void>;
  signInDemo: (profile: UserProfile) => Promise<void>;
  signOut: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  resetPassword: (email: string) => Promise<void>;
  isAdmin: boolean;
  adminEmails: string[];
  isConfigured: boolean;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  session: null,
  loading: false,
  signUp: async () => {},
  signIn: async () => {},
  signInWithGoogle: async () => {},
  signInDemo: async () => {},
  signOut: async () => {},
  getAccessToken: async () => null,
  resetPassword: async () => {},
  isAdmin: false,
  adminEmails: [],
  isConfigured: false,
});

export const useAuth = () => useContext(AuthContext);

function activateUserWorkspace(userId: string): boolean {
  const workspaceVersion = 'auth-workspace-v3';
  const prevUserId = safeStorage.get('crewnest_active_auth_id');
  const prevVersion = safeStorage.get('crewnest_auth_workspace_version');

  // Point the DB layer at this user's isolated database
  setActiveUserId(userId);

  if (prevUserId === userId && prevVersion === workspaceVersion) return false;

  // Switching to a different user — clear shared localStorage keys
  safeStorage.remove('crewnest_demo_user');
  safeStorage.remove('crewnest_last_sync');
  safeStorage.remove('crewnest_cert_version');
  safeStorage.set('crewnest_active_auth_id', userId);
  safeStorage.set('crewnest_auth_workspace_version', workspaceVersion);
  return true;
}

function canUseLocalDemoSession(): boolean {
  return process.env.NODE_ENV !== 'production' && safeStorage.get('crewnest_screenshot_demo') === '1';
}

// Static — computed once from env, never changes at runtime
const supabase = createClient();
const isConfigured = supabase !== null;
const adminEmails = (process.env.NEXT_PUBLIC_ADMIN_EMAILS || '')
  .split(',')
  .map((e) => e.trim().toLowerCase())
  .filter(Boolean);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = useMemo(
    () => !!user?.email && adminEmails.includes(user.email.toLowerCase()),
    [user]
  );

  const shouldPreserveAuthFlowRoute = useCallback(() => {
    if (typeof window === 'undefined') return false;
    return window.location.pathname === '/auth/callback' || window.location.pathname === '/reset-password';
  }, []);

  useEffect(() => {
    if (!supabase || canUseLocalDemoSession()) {
      try {
        const stored = safeStorage.get('crewnest_demo_user');
        if (stored) {
          const demoProfile = JSON.parse(stored);
          setActiveUserId('demo');
          setProfile(demoProfile);
          setUser({ id: demoProfile.id, email: demoProfile.email });
        }
      } catch {
        safeStorage.remove('crewnest_demo_user');
      }
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        if (activateUserWorkspace(session.user.id) && !shouldPreserveAuthFlowRoute()) {
          window.location.replace('/');
          return;
        }
        fetchProfile(session.user.id).finally(() => setLoading(false));
        return;
      }
      safeStorage.remove('crewnest_demo_user');
      setProfile(null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        if (activateUserWorkspace(session.user.id) && !shouldPreserveAuthFlowRoute()) {
          window.location.replace('/');
          return;
        }
        fetchProfile(session.user.id);
      }
      else setProfile(null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [shouldPreserveAuthFlowRoute]);

  const fetchProfile = useCallback(async (userId: string) => {
    if (!supabase) return;
    const { data } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single();
    if (data) setProfile(data);
  }, []);

  const signUp = useCallback(async (email: string, password: string, metadata: any = {}) => {
    if (!supabase) throw new Error('Supabase not configured');
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata.fullName || '',
          cdc_number: metadata.cdcNumber || null,
          rank: metadata.rank || null,
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    const needsEmailConfirmation = !data.session;
    if (data.user && data.session) {
      const profileRow: UserProfile = {
        id: data.user.id,
        full_name: metadata.fullName || '',
        email,
        cdc_number: metadata.cdcNumber || null,
        rank: metadata.rank || null,
      };
      await (supabase as any).from('user_profiles').upsert(profileRow);
    }
    return { ...data, needsEmailConfirmation };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    if (!supabase) throw new Error('Supabase not configured');
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  }, []);

  const signInWithGoogle = useCallback(async () => {
    if (!supabase) throw new Error('Supabase not configured');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) throw error;
  }, []);

  const signInDemo = useCallback(async (demoProfile: UserProfile) => {
    if (supabase) await supabase.auth.signOut();
    setActiveUserId('demo');
    safeStorage.set('crewnest_demo_user', JSON.stringify(demoProfile));
    setProfile(demoProfile);
    setUser({ id: demoProfile.id, email: demoProfile.email });
    setSession(null);
  }, []);

  const signOut = useCallback(async () => {
    if (!supabase) {
      safeStorage.remove('crewnest_demo_user');
      setProfile(null);
      setUser(null);
      setSession(null);
      return;
    }
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }, []);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!supabase) return null;
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  }, []);

  const resetPassword = useCallback(async (email: string): Promise<void> => {
    if (!supabase) throw new Error('Supabase not configured');
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback?type=recovery`,
    });
    if (error) throw error;
  }, []);

  const value = useMemo(
    () => ({ user, profile, session, loading, signUp, signIn, signInWithGoogle, signInDemo, signOut, getAccessToken, resetPassword, isAdmin, adminEmails, isConfigured }),
    [user, profile, session, loading, signUp, signIn, signInWithGoogle, signInDemo, signOut, getAccessToken, resetPassword, isAdmin]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
